import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Chip,
  Card,
  CardContent,
  Container,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as ManagementIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import useFormDraft from '../../../hooks/useFormDraft';
import FormDraftNotice from '../../common/FormDraftNotice';
import Loading from '../../common/Loading';
import Header from '../../common/Header';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import FeedbackMessage from '../../common/FeedbackMessage';
import { motion } from 'framer-motion';
import { TextField, InputAdornment } from '@mui/material';
import CommonSectionCard from '../../common/SectionCard';
import InfoField from '../../common/InfoField';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import SectionCard from '../../common/SectionCard';

// ─── Design tokens — cyberpunk dark system ────────────────────────────────────
const B = {
  600: '#2563EB',
  700: '#1D4ED8',
  900: '#1E3A8A',
};

const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse    { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in  { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping     { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }
`;


// Dark glass textarea / input field
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    color: alpha('#E2E8F0', 0.85),
    background: alpha(SURFACE_UP, 0.7),
    backdropFilter: 'blur(8px)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': { borderColor: alpha(NEON, 0.18) },
    '&:hover fieldset': { borderColor: alpha(NEON, 0.4) },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}`, animation: 'form-glow-in 0.3s ease' },
    '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
  },
  '& .MuiInputBase-input': {
    color: alpha('#E2E8F0', 0.85),
    '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 },
  },
  '& .MuiInputLabel-root': { fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45) },
  '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
  '& .MuiFormHelperText-root': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: alpha(NEON, 0.5) },
  '& .MuiFormHelperText-root.Mui-error': { color: '#F87171' },
};

const StyledField = ({ sx, ...props }) => <TextField {...props} sx={{ ...fieldSx, ...sx }} />;

const readOnlyFieldSx = {
  borderRadius: '12px',
  background: alpha(SURFACE_UP, 0.45),
  border: `1px solid ${alpha(NEON, 0.14)}`,
  boxShadow: `0 8px 28px ${alpha('#000', 0.28)}`,
  px: 2.5,
  py: 1.6,
};

const readOnlyLabelSx = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 1.6,
  textTransform: 'uppercase',
  color: alpha(NEON, 0.6),
  mb: 0.6,
};

const ReadOnlyField = ({ label, value, icon = null, multiline = false }) => (
  <InfoField icon={icon} label={label} value={value} />
);

// ─── Main Component ───────────────────────────────────────────────────────────
const ManagementForm = ({ isDetails = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = Boolean(id);
  const isReadOnly = Boolean(isDetails);

  const [error, setError]           = useState(null);
  const [loading, setLoading]       = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [touched, setTouched]       = useState({});
  const [dataLoading, setDataLoading] = useState(Boolean(id));
  const [formData, setFormData]     = useState({ name: '', description: '' });

  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [pendingUpdateRequest, setPendingUpdateRequest] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const cssId = 'mgmtform-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = FORM_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    if (id) fetchManagement();
  }, [id]);

  const applyDraft = useCallback((data) => {
    setFormData((prev) => ({ ...prev, ...data }));
  }, []);
  const getDraftSnapshot = useCallback(() => formData, [formData]);
  const formDraft = useFormDraft({
    formKey: 'management',
    userId: user?.id,
    isEditMode: isEditMode || isReadOnly,
    enabled: !dataLoading,
    getSnapshot: getDraftSnapshot,
    applySnapshot: applyDraft,
    resetForm: () => setFormData({ name: '', description: '' }),
  });

  const fetchManagement = async () => {
    try {
      setDataLoading(true);
      const response = await api.get(`/managements/${id}`);
      const management = response?.data?.data ?? response?.data ?? {};
      setFormData({ name: management.name || '', description: management.description || '' });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load management data. Please try again.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleBlur = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const validateForm = () => {
    if (!formData.name.trim()) { setError('Management name is required'); return false; }
    if (formData.name.length < 2) { setError('Management name must be at least 2 characters long'); return false; }
    return true;
  };

  const confirmUpdate = async (requestOverride = null) => {
    if (!validateForm()) {
      setConfirmUpdateDialogOpen(false);
      setPendingUpdateRequest(null);
      return;
    }

    const hasValidOverride =
      requestOverride &&
      typeof requestOverride === 'object' &&
      typeof requestOverride.method === 'string' &&
      typeof requestOverride.url === 'string';
    const requestData = hasValidOverride ? requestOverride : pendingUpdateRequest;
    if (!requestData) return;
    try {
      setLoading(true);
      setError(null);
      const { method, url, payload } = requestData;
      await api[method](url, payload);
      if (!isEditMode) formDraft.clearDraft();
      setSuccessMessage(`Management ${isEditMode ? 'updated' : 'created'} successfully!`);
      setTimeout(() => navigate('/managements', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) {
      if (err.response?.data?.errors) {
        setError(Object.values(err.response.data.errors).flat().join(', '));
      } else {
        setError(err.response?.data?.message || 'Failed to save management. Please try again.');
      }
    } finally {
      setLoading(false);
      setConfirmUpdateDialogOpen(false);
      setPendingUpdateRequest(null);
    }
  };

  const confirmDelete = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/managements/${id}`);
      setSuccessMessage('Management deleted successfully!');
      setTimeout(() => navigate('/managements', { state: { refreshList: Date.now() } }), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete management. Please try again.');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;

    const method = id ? 'put' : 'post';
    const url = id ? `/managements/${id}` : '/managements';
    const requestData = { method, url, payload: { ...formData } };

    if (isEditMode) {
      setPendingUpdateRequest(requestData);
      setConfirmUpdateDialogOpen(true);
      return;
    }

    if (!validateForm()) return;
    await confirmUpdate(requestData);
  };

  const isFormValid = formData.name.trim().length >= 2;

  if (dataLoading) return (
  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
    <Loading message="Loading management data" size={56} variant="primary" />
  </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      <Header
        title={isReadOnly ? 'Management Details' : isEditMode ? 'Edit Management' : 'Create New Management'}
        subtitle={
          isReadOnly
            ? 'View management information'
            : isEditMode
              ? 'Update management information and settings'
              : 'Add a new management level to your organization'
        }
        icon={ManagementIcon}
        showBackButton
        onBackClick={() => navigate('/managements')}
        backButtonText="Back to Managements"
        actionButton={
          isReadOnly ? (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate(`/managements/edit/${id}`)}
                sx={{
                  borderRadius: '10px', px: 2.5, py: 0.7,
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
                  background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.6)}`,
                  color: NEON,
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    background: alpha(NEON, 0.08), 
                    borderColor: NEON,
                    boxShadow: `0 0 12px ${alpha(NEON, 0.2)}`
                  },
                }}
              >
                Edit
              </Button>
              <Button
                startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  borderRadius: '10px', px: 2.5, py: 0.7,
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
                  background: 'transparent', border: `1.5px solid ${alpha('#F87171', 0.6)}`,
                  color: '#F87171',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    background: alpha('#F87171', 0.08), 
                    borderColor: '#F87171',
                    boxShadow: `0 0 12px ${alpha('#F87171', 0.2)}`
                  },
                }}
              >
                Delete
              </Button>
            </Box>
          )
          : null
        }
      />

      <FeedbackMessage
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage('')}
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.9rem',
          fontWeight: 500,
          background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_MID, 0.95)})`,
          border: `1px solid ${alpha(NEON3, 0.35)}`,
          boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`,
          color: alpha(NEON3, 0.95),
        }}
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.9rem',
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          color: '#fca5a5',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
        }}
      />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Management Information */}
          <SectionCard title="Management Information" icon={<ManagementIcon />} delay={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {isReadOnly ? (
                <>
                  <ReadOnlyField label="Management Name" value={formData.name} icon={<ManagementIcon />} />
                  <ReadOnlyField label="Description" value={formData.description} icon={<DescriptionIcon />} multiline />
                </>
              ) : (
                <>
                  <StyledField
                    fullWidth label="Management Name" name="name" value={formData.name}
                    onChange={handleChange} onBlur={() => handleBlur('name')} required
                    sx={{
                      '& .MuiFormLabel-asterisk': {
                        color: 'red',
                      },
                    }}
                    error={touched.name && !formData.name.trim()}
                    helperText={touched.name && !formData.name.trim() ? 'Management name is required' : 'Enter a unique name for this management level'}
                    InputProps={{ startAdornment: <InputAdornment position="start"><ManagementIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                  <StyledField
                    fullWidth label="Description" name="description" value={formData.description}
                    onChange={handleChange} multiline rows={4}
                    placeholder="Enter a detailed description of the management's role and responsibilities..."
                    helperText={`${formData.description.length} characters`}
                    InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}><DescriptionIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                </>
              )}
            </Box>
          </SectionCard>

          {isReadOnly ? null : (
            <SubmitActionsCard
              missingRequiredFields={isEditMode ? null : [formData.name.trim().length < 2 && 'Management Name (minimum 2 characters)'].filter(Boolean)}
              readyStatusText="All required fields are complete. Ready to create management."
              statusText={
                isEditMode
                    ? 'Save changes to update this management.'
                    : 'Complete the form to add a new management.'
              }
              cancelLabel='Cancel'
              onCancel={() => navigate('/managements')}
              cancelDisabled={loading}
              submitLabel={isEditMode ? 'Update Management' : 'Create Management'}
              submitDisabled={loading || confirmUpdateDialogOpen || !isFormValid}
              loading={loading}
              submitStartIcon={<SaveIcon />}
              showSubmit
              submitType="submit"
              showSaveDraft={formDraft.canSaveDraft}
              onSaveDraft={formDraft.saveDraft}
              draftSavedAt={formDraft.lastSavedAt}
            />
          )}
        </Box>
      </form>
      <ConfirmDeleteDialog
        open={confirmUpdateDialogOpen}
        onClose={() => {
          setConfirmUpdateDialogOpen(false);
          setPendingUpdateRequest(null);
        }}
        onConfirm={confirmUpdate}
        variant="edit"
        title="Confirm Update"
        itemType="management"
        itemName={formData.name}
        description="This action will update the management details and cannot be undone."
        confirmLabel="Update Management"
        loading={loading}
      />
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        itemType="management"
        itemName={formData.name}
        description="This action cannot be undone and will permanently remove this management data."
        confirmLabel="Delete Management"
        loading={loading}
      />
    </Container>
  );
};

export default ManagementForm;
